import { useCallback, useEffect, useRef, useState } from "react";

import { isYleAppEmbed } from "../../helpers/commonUtils";
import {
  createTunnusSdkBroadcastChannel,
  yleTunnusSDKExists
} from "../../helpers/yleTunnus";
import { getLoginData } from "../../service/login-api";
import { LoginData } from "../../types/common";

const defaultLoginData = {
  loggedIn: false,
  residenceInFinland: false,
  userId: undefined
};

const BCChanelTimeout = 10000;

const broadcastChannelAvailable = () =>
  typeof window !== "undefined" &&
  typeof window.BroadcastChannel !== "undefined" &&
  !isYleAppEmbed() && // Do not use broadcastChannel on yle app (= webview)
  yleTunnusSDKExists();

export const useLoginData = ({
  requireLogin
}: {
  requireLogin: boolean;
}): LoginData | undefined => {
  const loginInitialized = useRef(false);
  const loginDataFetched = useRef(false);
  const allowBroadcastChannelRef = useRef(false);
  const broadcastChannelRef = useRef<BroadcastChannel | null>(null);

  const [loginData, setLoginData] = useState<LoginData>();

  const clearBroadcastChannel = useCallback(() => {
    if (broadcastChannelRef.current) {
      broadcastChannelRef.current.onmessage = null;
      broadcastChannelRef.current.close();
      broadcastChannelRef.current = null;
    }
  }, []);

  const addBroadcastChannel = useCallback(() => {
    if (allowBroadcastChannelRef.current && !loginDataFetched.current) {
      clearBroadcastChannel();
      broadcastChannelRef.current =
        createTunnusSdkBroadcastChannel(setLoginData);
    }
  }, [clearBroadcastChannel, setLoginData]);

  const loadLoginData = useCallback(async () => {
    if (!requireLogin) {
      // If login is not required, use default (not logged in) data
      setLoginData({ ...defaultLoginData });
    } else if (broadcastChannelAvailable()) {
      allowBroadcastChannelRef.current = true;
      addBroadcastChannel();
    } else {
      //  BroadcastChannel might not be available in all browsers nor outside yle.fi domain
      try {
        setLoginData(await getLoginData());
      } catch {
        console.error("Login api call failed");
        setLoginData({ ...defaultLoginData });
      }
    }
  }, [addBroadcastChannel, requireLogin, setLoginData]);

  useEffect(() => {
    if (!loginInitialized.current) {
      void loadLoginData();
      loginInitialized.current = true;
    }
  }, [loadLoginData]);

  useEffect(() => {
    // BroadcastChannel must be closed on pagehide event
    // because it might affect the browser's bfcache
    if (!loginData) {
      window.addEventListener("pagehide", clearBroadcastChannel);
      window.addEventListener("pageshow", addBroadcastChannel);
    }

    return () => {
      window.removeEventListener("pagehide", clearBroadcastChannel);
      window.removeEventListener("pageshow", addBroadcastChannel);
    };
  }, [addBroadcastChannel, clearBroadcastChannel, loginData]);

  useEffect(() => {
    if (loginData) {
      // After login data fetched, clear the broadcast channel, we do not need to listen it anymore
      loginDataFetched.current = true;
      clearBroadcastChannel();
    }
  }, [clearBroadcastChannel, loginData]);

  useEffect(() => {
    // After this component is rendered, we wait 10s to get the login data
    // If login data is not received, show an error and behave like user is not logged in
    const timeout = setTimeout(() => {
      if (!loginDataFetched.current) {
        console.error("Player didn't get the login data");
        setLoginData({ ...defaultLoginData });
      }
    }, BCChanelTimeout);

    return () => {
      clearTimeout(timeout);
    };
  }, []);

  return loginData;
};
